import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { userAPI } from '../api';
import api from '../api';
import { t } from '../i18n';

const Profile = () => {
  const { user, updateUser } = useContext(AuthContext);

  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    description: '',
    contact_info: '',
    profile_photo: null,
  });

  const [message, setMessage] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [tempFile, setTempFile] = useState(null);
  const [tempUrl, setTempUrl] = useState('');

  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
  const [initialScale, setInitialScale] = useState(1);
  const [cropInit, setCropInit] = useState(false);

  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startDrag, setStartDrag] = useState({
    x: 0,
    y: 0,
    sx: 0,
    sy: 0,
  });

  const [zoom, setZoom] = useState(1);

  const imgRef = React.useRef(null);

  // Resolve backend media URLs to absolute URLs.
  const resolvePhotoUrl = (url) => {
    if (!url) return '';

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    const base =
      api && api.defaults && api.defaults.baseURL
        ? api.defaults.baseURL
        : '';

    const origin = base.replace(/\/api\/?$/, '');

    if (url.startsWith('/')) {
      return `${origin}${url}`;
    }

    return `${origin}/${url}`;
  };

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        description: user.description || '',
        contact_info: user.contact_info || '',
        profile_photo: null,
      });

      setPreviewUrl(resolvePhotoUrl(user.profile_photo) || '');
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Validate and select profile photo.
  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];

    if (!file) return;

    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
    ];

    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setMessage(t('invalidProfilePhotoType'));
      setTimeout(() => setMessage(''), 5000);
      e.target.value = '';
      return;
    }

    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const ext = file.name.split('.').pop().toLowerCase();

    if (!allowedExtensions.includes(ext)) {
      setMessage(
        `${t('invalidFileExtension')} '.${ext}'. ${t(
          'allowedExtensions'
        )}: ${allowedExtensions.join(', ')}`
      );

      setTimeout(() => setMessage(''), 5000);
      e.target.value = '';
      return;
    }

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      setMessage(
        `${t('fileTooLarge')} (${(file.size / (1024 * 1024)).toFixed(
          1
        )} MB). ${t('maximumFileSize')}: 5 MB.`
      );

      setTimeout(() => setMessage(''), 5000);
      e.target.value = '';
      return;
    }

    setTempFile(file);
    setCropModalOpen(true);
  };

  // Create and clean up temporary image URL.
  useEffect(() => {
    if (!tempFile) {
      setTempUrl('');
      return;
    }

    const url = URL.createObjectURL(tempFile);

    setTempUrl(url);
    setCropInit(false);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [tempFile]);

  // Initialize crop position after image loads.
  const onImageLoad = (e) => {
    const img = e.target;

    const nw = img.naturalWidth;
    const nh = img.naturalHeight;

    setImgNatural({ w: nw, h: nh });

    const C = 300;

    const initScale = Math.max(C / nw, C / nh);

    setInitialScale(initScale);

    if (!cropInit) {
      const displayW = nw * initScale;
      const displayH = nh * initScale;

      const startX = (C - displayW) / 2;
      const startY = (C - displayH) / 2;

      const minX = Math.min(0, C - displayW);
      const minY = Math.min(0, C - displayH);

      const clampedX = Math.max(minX, Math.min(0, startX));
      const clampedY = Math.max(minY, Math.min(0, startY));

      setPos({
        x: clampedX,
        y: clampedY,
      });

      setZoom(1);
      setCropInit(true);
    }
  };

  // Pointer Events support mouse, touchscreen, and stylus.
  const handlePointerDown = (e) => {
    if (!cropInit) return;

    e.preventDefault();

    e.currentTarget.setPointerCapture(e.pointerId);

    setDragging(true);

    setStartDrag({
      x: e.clientX,
      y: e.clientY,
      sx: pos.x,
      sy: pos.y,
    });
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;

    e.preventDefault();

    const dx = e.clientX - startDrag.x;
    const dy = e.clientY - startDrag.y;

    const C = 300;

    const tentativeX = startDrag.sx + dx;
    const tentativeY = startDrag.sy + dy;

    const displayW = imgNatural.w * initialScale * zoom;
    const displayH = imgNatural.h * initialScale * zoom;

    const minX = Math.min(0, C - displayW);
    const minY = Math.min(0, C - displayH);

    const clampedX = Math.max(minX, Math.min(0, tentativeX));
    const clampedY = Math.max(minY, Math.min(0, tentativeY));

    setPos({
      x: clampedX,
      y: clampedY,
    });
  };

  const handlePointerUp = (e) => {
    setDragging(false);

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  // Handle zoom slider.
  const handleZoomChange = (e) => {
    let z = parseFloat(e.target.value);

    const minZoom = 1;

    if (isNaN(z) || z < minZoom) {
      z = minZoom;
    }

    const C = 300;

    const displayW = imgNatural.w * initialScale * z;
    const displayH = imgNatural.h * initialScale * z;

    const minX = Math.min(0, C - displayW);
    const minY = Math.min(0, C - displayH);

    const clampedX = Math.max(minX, Math.min(0, pos.x));
    const clampedY = Math.max(minY, Math.min(0, pos.y));

    setZoom(z);

    setPos({
      x: clampedX,
      y: clampedY,
    });
  };

  // Compress image before uploading.
  const compressImageToSize = async (
    canvas,
    targetSizeBytes = 2.5 * 1024 * 1024
  ) => {
    let quality = 0.9;
    let blob = null;

    while (quality > 0.1) {
      blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', quality);
      });

      if (!blob) {
        throw new Error('Image compression failed');
      }

      if (blob.size <= targetSizeBytes) {
        return blob;
      }

      quality -= 0.1;
    }

    return blob;
  };

  // Confirm and export cropped image.
  const confirmCrop = async () => {
    if (!tempFile || !cropInit) return;

    try {
      const C = 300;

      const appliedScale = initialScale * zoom;

      const sx = Math.max(0, -pos.x / appliedScale);
      const sy = Math.max(0, -pos.y / appliedScale);

      const sWidth = Math.min(
        imgNatural.w - sx,
        C / appliedScale
      );

      const sHeight = Math.min(
        imgNatural.h - sy,
        C / appliedScale
      );

      const canvas = document.createElement('canvas');

      canvas.width = C;
      canvas.height = C;

      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Canvas is not supported');
      }

      const image = new Image();
      const imageUrl = URL.createObjectURL(tempFile);

      try {
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
          image.src = imageUrl;
        });

        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, C, C);

        ctx.drawImage(
          image,
          sx,
          sy,
          sWidth,
          sHeight,
          0,
          0,
          C,
          C
        );
      } finally {
        URL.revokeObjectURL(imageUrl);
      }

      const blob = await compressImageToSize(
        canvas,
        2.5 * 1024 * 1024
      );

      const croppedFile = new File(
        [blob],
        'profile_photo.jpg',
        { type: 'image/jpeg' }
      );

      setFormData((prev) => ({
        ...prev,
        profile_photo: croppedFile,
      }));

      const url = URL.createObjectURL(croppedFile);

      setPreviewUrl((previousUrl) => {
        if (previousUrl && previousUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previousUrl);
        }

        return url;
      });

      setCropModalOpen(false);
      setTempFile(null);
    } catch (err) {
      console.error('Crop error:', err);

      setMessage('Failed to crop image. Please try another image.');

      setTimeout(() => setMessage(''), 5000);
    }
  };

  const cancelCrop = () => {
    setCropModalOpen(false);
    setTempFile(null);
    setDragging(false);
  };

  // Remove profile photo.
  const handleRemovePhoto = async () => {
    try {
      await userAPI.updateProfile(user.id, {
        profile_photo: null,
      });

      await updateUser();

      setPreviewUrl('');

      setFormData((prev) => ({
        ...prev,
        profile_photo: null,
      }));

      setMessage(t('profilePhotoRemoved'));

      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(t('failedToRemoveProfilePhoto'));

      setTimeout(() => setMessage(''), 3000);
    }
  };

  // Save profile changes.
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await userAPI.updateProfile(user.id, formData);

      await updateUser();

      setMessage(t('profileUpdatedSuccessfully'));

      setEditing(false);

      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(t('failedToUpdateProfile'));
    }
  };

  if (!user) {
    return <div>{t('loading')}</div>;
  }

  return (
    <div className="container profile-page">
      <h1 style={{ textAlign: 'center' }}>
        {t('profile')}
      </h1>

      {message && (
        <div style={{ color: 'green', marginBottom: '10px' }}>
          {message}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <p>
          <strong>{t('username')}:</strong> {user.username}
        </p>

        <p style={{ marginBottom: '6px' }}>
          <strong>{t('email')}:</strong> {user.email}
        </p>

        <small
          style={{
            display: 'block',
            fontStyle: 'italic',
            color: '#666',
          }}
          title={t('privateEmailExplanation')}
        >
          {t('yourEmailIsPrivate')}
        </small>
      </div>

      <div style={{ marginTop: '10px' }}>
        {editing ? (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '12px' }}>
              {previewUrl ? (
                <img
                  className="profile-photo"
                  src={previewUrl}
                  alt={
                    user.first_name || user.username || ''
                  }
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: 'cover',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 120,
                    height: 120,
                    background: '#ddd',
                    borderRadius: '50%',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                />
              )}

              <div className="form-group">
                <label>{t('profilePhoto')}</label>

                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />

                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleRemovePhoto}
                    disabled={!previewUrl && !user.profile_photo}
                  >
                    {t('removePhoto')}
                  </button>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>{t('firstName')}</label>

              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>{t('lastName')}</label>

              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>{t('description')}</label>

              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={t('tellPeopleAboutYourself')}
                maxLength={500}
              />

              <div
                style={{
                  textAlign: 'right',
                  color: '#666',
                  fontSize: '12px',
                  marginTop: '6px',
                }}
              >
                {formData.description.length}/500
              </div>
            </div>

            <div className="form-group">
              <label>{t('whereToContactMe')}</label>

              <textarea
                name="contact_info"
                value={formData.contact_info}
                onChange={handleChange}
                placeholder={t('contactInfoPlaceholder')}
                maxLength={500}
              />

              <div
                style={{
                  textAlign: 'right',
                  color: '#666',
                  fontSize: '12px',
                  marginTop: '6px',
                }}
              >
                {formData.contact_info.length}/500
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ marginRight: '10px' }}
            >
              {t('save')}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditing(false)}
            >
              {t('cancel')}
            </button>
          </form>
        ) : (
          <div>
            {user.profile_photo ? (
              <img
                className="profile-photo"
                src={resolvePhotoUrl(user.profile_photo)}
                alt={user.first_name || user.username || ''}
                style={{
                  width: 120,
                  height: 120,
                  objectFit: 'cover',
                  display: 'block',
                  marginBottom: '8px',
                }}
              />
            ) : null}

            <p>
              <strong>{t('firstName')}:</strong>{' '}
              {user.first_name || t('notSet')}
            </p>

            <p>
              <strong>{t('lastName')}:</strong>{' '}
              {user.last_name || t('notSet')}
            </p>

            <p>
              <strong>{t('description')}:</strong>{' '}
              {user.description || t('notSet')}
            </p>

            <p>
              <strong>{t('whereToContactMe')}:</strong>
            </p>

            <pre
              style={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
              }}
            >
              {user.contact_info || t('notSet')}
            </pre>

            <button
              className="btn btn-primary"
              onClick={() => setEditing(true)}
            >
              {t('editProfile')}
            </button>
          </div>
        )}
      </div>

      {/* Image crop modal */}
      {cropModalOpen && (
        <div className="crop-modal">
          <div
            className="crop-backdrop"
            onClick={cancelCrop}
          />

          <div className="crop-panel">
            <div
              className="crop-area"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              style={{
                width: 300,
                height: 300,
                position: 'relative',
                overflow: 'hidden',
                touchAction: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                cursor: dragging ? 'grabbing' : 'grab',
              }}
            >
              {tempFile && (
                <img
                  ref={imgRef}
                  src={tempUrl}
                  alt={t('imageToCrop')}
                  onLoad={onImageLoad}
                  draggable={false}
                  style={{
                    position: 'absolute',
                    left: `${pos.x}px`,
                    top: `${pos.y}px`,
                    width: `${Math.max(
                      1,
                      imgNatural.w * initialScale * zoom
                    )}px`,
                    height: `${Math.max(
                      1,
                      imgNatural.h * initialScale * zoom
                    )}px`,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    pointerEvents: 'none',
                  }}
                />
              )}

              <div className="crop-circle" />
            </div>

            <div
              style={{
                marginTop: 12,
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <label style={{ fontSize: 14 }}>
                {t('zoom')}
              </label>

              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={zoom}
                onChange={handleZoomChange}
              />
            </div>

            <div
              style={{
                marginTop: 12,
                display: 'flex',
                gap: 8,
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={cancelCrop}
              >
                {t('cancel')}
              </button>

              <button
                className="btn btn-primary"
                onClick={confirmCrop}
                disabled={!cropInit}
              >
                {t('usePhoto')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;